import axios from "axios";
import constant from "../constant";
import { canQueueOffline, enqueueRequest, flushQueue } from "../utils/offlineQueue";
import { getAuthHeader, clearAuthSession, parseApiErrorMessage } from "../utils";
import { MSG } from "../constants/userMessages";
import { getQueueSummary } from "../utils/offlineQueue";
import { setSyncState } from "../utils/syncState";

let flushing = false;
let refreshPromise = null;

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const createRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `request-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const getHeader = (headers, name) => {
  if (typeof headers?.get === "function") return headers.get(name);
  const key = Object.keys(headers || {}).find((item) => item.toLowerCase() === name.toLowerCase());
  return key ? headers[key] : undefined;
};

const setHeader = (headers, name, value) => {
  if (typeof headers?.set === "function") headers.set(name, value);
  else headers[name] = value;
};

const AUTH_FAILURE_RE =
  /unauthorized|token missing|session expired|please login again/i;

function redirectToLogin() {
  clearAuthSession();
  const loginUrl = constant.nonAuthedcomponent?.login?.url || "/login";
  if (!window.location.pathname.startsWith(loginUrl)) {
    window.location.assign(loginUrl);
  }
}

const isPublicAuthRequest = (url = "") =>
  /\/v2\/(?:login(?:\/|$)|auth\/sso\/)/.test(String(url));

async function refreshSession() {
  if (!refreshPromise) {
    const base = window.RUNTIME_CONFIG?.API_BASE_URL ?? "";
    refreshPromise = axios
      .post(`${base}/v2/login/refresh`, {}, {
        withCredentials: true,
        __skipAuthRefresh: true,
      })
      .then((response) => {
        if (!response?.data?.status) throw new Error("Session refresh failed");
        return response;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function isNetworkFailure(error) {
  if (!error) return false;
  if (error.code === "ERR_NETWORK") return true;
  if (!error.response && !navigator.onLine) return true;
  return false;
}

async function flushOfflineQueue() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  setSyncState({ online: true, syncing: true, state: "syncing", retrying: false });
  try {
    const result = await flushQueue(async (item) => {
      const auth = getAuthHeader();
      const headers = {
        ...(item.headers || {}),
        ...auth.headers,
        "X-Request-Id": item.request_id,
        "Idempotency-Key": item.request_id,
      };
      if (item.body instanceof FormData) {
        Object.keys(headers).forEach((name) => {
          if (name.toLowerCase() === "content-type") delete headers[name];
        });
      }
      await axios({
        method: item.method,
        url: item.url,
        data: item.body,
        headers,
        withCredentials: true,
        __offlineQueued: true,
      });
    });
    const summary = await getQueueSummary();
    const completedAt = result.failed === 0 ? new Date().toISOString() : undefined;
    setSyncState({
      online: true,
      syncing: false,
      queueCount: summary.count,
      failedCount: summary.failedCount,
      state: result.failed > 0 ? "failed" : summary.count > 0 ? "queued" : "synced",
      ...(completedAt ? { lastSuccessfulAt: completedAt } : {}),
    });
    window.dispatchEvent(new CustomEvent("elog-sync-flush"));
  } finally {
    flushing = false;
    if (!navigator.onLine) {
      setSyncState({ online: false, syncing: false, state: "offline" });
    }
  }
}

export function setupAxiosInterceptors() {
  axios.interceptors.request.use((config) => {
    const method = String(config.method || "GET").toUpperCase();
    if (!MUTATING.has(method)) return config;
    config.headers = config.headers || {};
    const stableRequestId =
      getHeader(config.headers, "Idempotency-Key") ||
      getHeader(config.headers, "X-Request-Id") ||
      createRequestId();
    setHeader(config.headers, "X-Request-Id", stableRequestId);
    setHeader(config.headers, "Idempotency-Key", stableRequestId);
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const authMessage = parseApiErrorMessage(error?.response?.data, "");
      const failedConfig = error?.config;

      if (status === 401 || (status === 403 && AUTH_FAILURE_RE.test(authMessage))) {
        if (
          failedConfig &&
          !failedConfig.__skipAuthRefresh &&
          !failedConfig.__authRetried &&
          !isPublicAuthRequest(failedConfig.url)
        ) {
          try {
            await refreshSession();
            failedConfig.__authRetried = true;
            failedConfig.withCredentials = true;
            return axios(failedConfig);
          } catch {
            // The refresh endpoint rotates/clears cookies; continue to sign-out.
          }
        }
        redirectToLogin();
        return Promise.reject(error);
      }

      if (status === 429) {
        const config = error?.config;
        // A queued write remains durable until the server actually accepts it.
        if (config?.__offlineQueued) return Promise.reject(error);
        return Promise.resolve({
          data: {
            status: false,
            allowed: false,
            error: {
              message: parseApiErrorMessage(
                error?.response?.data,
                "Too many requests. Please wait a moment and try again."
              ),
            },
          },
          status: 429,
          statusText: error?.response?.statusText || "Too Many Requests",
          headers: error?.response?.headers || {},
          config,
        });
      }

      const config = error?.config;
      const method = String(config?.method || "").toUpperCase();

      // Read calls: return API error body instead of uncaught runtime overlay (403 vessel scope, etc.)
      if (
        config &&
        !MUTATING.has(method) &&
        isNetworkFailure(error)
      ) {
        return Promise.resolve({
          data: {
            status: false,
            allowed: false,
            error: {
              message: MSG.network,
            },
          },
          status: 0,
          statusText: "Network Error",
          headers: {},
          config,
        });
      }

      // Soft-resolve JSON GETs so callers can read `{ status: false }` without try/catch.
      // Blob downloads must still reject — otherwise error JSON is treated as a file/PDF.
      if (
        config &&
        !MUTATING.has(method) &&
        status >= 400 &&
        status < 500 &&
        error.response?.data &&
        config.responseType !== "blob"
      ) {
        return Promise.resolve({
          data: error.response.data,
          status,
          statusText: error.response.statusText,
          headers: error.response.headers,
          config,
        });
      }

      if (
        config &&
        !MUTATING.has(method) &&
        status === 503 &&
        error.response?.data?.errorCode === "DEV_NO_DB_MODE" &&
        config.responseType !== "blob"
      ) {
        return Promise.resolve({
          data: error.response.data,
          status,
          statusText: error.response.statusText,
          headers: error.response.headers,
          config,
        });
      }

      if (
        config &&
        !config.__offlineQueued &&
        canQueueOffline({ method: config.method, url: config.url }) &&
        isNetworkFailure(error)
      ) {
        const url = config.url || "";
        await enqueueRequest({
          method: config.method,
          url,
          data: config.data,
          headers: config.headers,
          requestId:
            getHeader(config.headers, "Idempotency-Key") ||
            getHeader(config.headers, "X-Request-Id"),
        });
        const summary = await getQueueSummary();
        const trulyOffline = !navigator.onLine;
        setSyncState({
          online: !trulyOffline,
          apiReachable: false,
          syncing: false,
          queueCount: summary.count,
          failedCount: summary.failedCount,
          state: trulyOffline ? "offline" : "queued",
        });
        config.__offlineQueued = true;
        // Queued ≠ saved. Do not present as a successful API write.
        return Promise.resolve({
          data: {
            status: false,
            allowed: true,
            queued: true,
            offline: trulyOffline,
            error: {
              message:
                trulyOffline
                  ? "Offline. This change is safely queued and will sync when connectivity returns."
                  : "The vessel server is temporarily unreachable. This change is safely queued and retry will begin automatically.",
            },
            result: { queued: true, offline: trulyOffline, synced: false },
          },
          status: 202,
          config,
        });
      }

      return Promise.reject(error);
    }
  );

  window.addEventListener("online", flushOfflineQueue);
  window.addEventListener("offline", () =>
    setSyncState({ online: false, apiReachable: false, syncing: false, state: "offline" })
  );
  if (navigator.onLine) {
    // Defer past first paint / dashboard query burst
    setTimeout(() => flushOfflineQueue(), 8000);
  }
}
